import { EVENT_ACCESS_CHECKOUT_APPEARANCE } from "../music-access-flow/checkout-appearance";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

type Instance = {
  destroy: () => void;
  on: (event: string, callback: (payload?: unknown) => void) => () => void;
  update: (options: { minHeight?: number }) => void;
};
type FrameOptions = {
  organizer: string;
  ancestors?: string[];
  artist?: string;
  appearance?: unknown;
  identity?: "host";
  contact?: "fixed";
};
type SessionOptions = {
  session: string;
  attestation?: string | Promise<string | null>;
  checkoutToken?: (request: CheckoutTokenRequest) => Promise<string>;
  timing?: { requestedAt?: number; sessionAt?: number };
};
type Loader = {
  mount: (element: Element, options: FrameOptions & SessionOptions) => Instance;
  /** Absent from a checkout.js from before preloading, which a browser may
   *  still hold (it is cached for five minutes). */
  preload?: (
    element: Element,
    options: FrameOptions,
  ) => { start: (options: SessionOptions) => Instance; destroy: () => void };
};

/**
 * Where the page that mounts the drop-in is itself shown, when that is in a
 * frame on another website (the Link in Bio embed): those websites' origins
 * and the Link in Bio slug of the artist who lists them. The checkout's
 * Worker lets its frame through only for a website that artist lists.
 */
export type CheckoutEmbedSurroundings = { artist: string; ancestors: string[] };

const Surroundings = createContext<CheckoutEmbedSurroundings | null>(null);

/** Says, for every drop-in below it, where this page is shown. */
export const CheckoutEmbedSurroundingsProvider = Surroundings.Provider;

/** What the drop-in asks the page for when the page answers for the buyer:
 *  a checkout action token for the mounted session. */
export type CheckoutTokenRequest = {
  sessionId: string;
  consents: string[];
  reverify: boolean;
};

/** What the drop-in reports when the tickets are issued, for a host that
 *  draws its own success screen. */
export type EngineCheckoutSucceeded = {
  /** The total as the buyer saw it, e.g. "£24.00". */
  total_label: string;
  /** "Visa 4242", "Apple Pay", … or null when the drop-in cannot say. */
  method_label: string | null;
  buyer_email: string | null;
  /** Host mode: where the drop-in drew its final status (the tick and
   *  "You're going") as it ended, in this page's viewport — for a success
   *  screen that takes them over in place. */
  status_origin: EngineStatusOrigin | null;
};

export type EngineStatusRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export type EngineStatusOrigin = {
  mark: EngineStatusRect;
  words: EngineStatusRect;
};

/** The drop-in's status rectangles (its own viewport, already validated by
 *  the loader) moved into this page's viewport, where its frame sits. */
function onPage(
  origin: EngineStatusOrigin | null | undefined,
  frame: Element | null | undefined,
): EngineStatusOrigin | null {
  if (!origin || !frame) return null;
  const at = frame.getBoundingClientRect();
  const move = (r: EngineStatusRect) => ({
    ...r,
    x: r.x + at.left,
    y: r.y + at.top,
  });
  return { mark: move(origin.mark), words: move(origin.words) };
}

/**
 * Mounts the ticketing engine's hosted checkout for a session: Link in
 * Bio's sheets, and the Lissen app's tickets sheet on the web.
 *
 * It can also be there before the session is: given no secret yet, it
 * makes the drop-in's frame at once (`LissenCheckout.preload`), so the
 * frame's page, code and handshake are done while the session is being
 * made, and starts that frame with the first secret it is given. The frame
 * is made here, where it is shown, and never moved: moving a frame loads it
 * again.
 */
export function EngineCheckoutEmbed({
  url,
  secret,
  onError,
  onHold,
  onPaying,
  origin,
  identity,
  contact,
  appearance = EVENT_ACCESS_CHECKOUT_APPEARANCE,
  onSucceeded,
  onChangeContact,
  onPaid,
  onExpired,
  placeholder,
  attestation,
  checkoutToken,
  timing,
}: {
  /** The session's hosted page (`…/checkout/{organizer}`, its fragment
   *  aside): the drop-in's origin and the organizer's slug. Before there is
   *  a session, the organizer's page itself. */
  url: string;
  /**
   * The session's client secret. Null: no session yet — the frame is made
   * now and started with the first secret given. Any other change of
   * secret is a new frame.
   */
  secret: string | null;
  onError: (message: string) => void;
  /** The hold deadline, once the cart is loaded: the sheet shows the countdown. */
  onHold?: (expiresAt: string) => void;
  /** The buyer pressed pay (true), or a payment came back refused (false):
   *  the sheet hides its countdown while money is moving. */
  onPaying?: (paying: boolean) => void;
  /** Where to load the drop-in from instead of the session's own hosted
   *  origin — a developer's local checkout. The session stays the engine's. */
  origin?: string | null;
  /** `"host"`: this page already verified the buyer's email with the Lissen
   *  identity plane (and the session carries it). The drop-in shows no email
   *  field and no sign-in of its own. */
  identity?: "host";
  /** With `identity: "host"`: `"fixed"` when the address is the fan's own
   *  sign-in on this page (the Lissen app) and cannot be changed, so the
   *  drop-in draws no × on it and never asks `onChangeContact`. */
  contact?: "fixed";
  /** Drop-in appearance tokens; the dark fan sheet's by default. */
  appearance?: unknown;
  /** The tickets are issued: what paid, how much, and where they went. */
  onSucceeded?: (payload: EngineCheckoutSucceeded) => void;
  /** The buyer pressed × on the tickets destination (host identity only). */
  onChangeContact?: () => void;
  /** The payment was accepted; tickets are being issued. */
  onPaid?: () => void;
  /** The hold ran out and the engine released the tickets. Without it,
   *  the expiry is reported through `onError`. */
  onExpired?: () => void;
  /** Host mode: drawn over the frame until the checkout has loaded. */
  placeholder?: ReactNode;
  /**
   * Host mode: the page's action token for this session, from the identity
   * it verified. Asked for as soon as there is a session and handed over
   * whenever it arrives, so the buyer is attested without the checkout
   * resolving them again — and the frame never waits on it to load.
   */
  attestation?: () => Promise<string | null>;
  /**
   * The page answers for the buyer: every token the drop-in asks for (the
   * first attestation, the card-reuse consent at pay, a renewed proof), and
   * the drop-in mounts no identity broker of its own. Takes precedence over
   * `attestation`, which is then only its first answer: passing both has
   * the first token minted while the frame loads, not once it asks. Given
   * or not for the frame's whole life, preloaded or not.
   */
  checkoutToken?: (request: CheckoutTokenRequest) => Promise<string>;
  /** When the buyer asked for this checkout and when the session came back
   *  (wall-clock milliseconds): the checkout times its load from the press. */
  timing?: { requestedAt: number; sessionAt?: number };
}) {
  // Latest callbacks without remounting the frame when a parent re-renders
  // with fresh closures.
  const succeeded = useRef(onSucceeded);
  succeeded.current = onSucceeded;
  const changeContact = useRef(onChangeContact);
  changeContact.current = onChangeContact;
  const paid = useRef(onPaid);
  paid.current = onPaid;
  const expired = useRef(onExpired);
  expired.current = onExpired;
  const attest = useRef(attestation);
  attest.current = attestation;
  const answer = useRef(checkoutToken);
  answer.current = checkoutToken;
  // Read when the session is handed over: the press it times is the one
  // this frame answers.
  const timed = useRef(timing);
  timed.current = timing;
  // Whether the page answers is fixed for a frame; the function may change.
  const answers = Boolean(checkoutToken);
  // Where this page is shown is fixed for the page's life.
  const surroundings = useContext(Surroundings);
  const artist = surroundings?.artist;
  const around = surroundings?.ancestors.join(" ") ?? "";
  const errored = useRef(onError);
  errored.current = onError;
  const held = useRef(onHold);
  held.current = onHold;
  const payingNow = useRef(onPaying);
  payingNow.current = onPaying;
  // The session as of the latest render, for a script that loads after it.
  const latest = useRef(secret);
  latest.current = secret;
  // The frame on the page: made for its session, or made ahead of one and
  // then started with the first session given. Any other change of session
  // is a new frame.
  const [frame, setFrame] = useState(() => ({
    id: 0,
    madeFor: secret,
    session: secret,
  }));
  if (secret !== frame.session)
    setFrame(
      frame.madeFor === null && frame.session === null
        ? { ...frame, session: secret }
        : { id: frame.id + 1, madeFor: secret, session: secret },
    );
  // The page the drop-in comes from: the same for every session of the
  // organizer, with or without one.
  const hosted = new URL(url);
  if (origin) hosted.href = `${origin}${hosted.pathname}`;
  const page = `${hosted.origin}${hosted.pathname}`;
  const [loadedFrame, setLoadedFrame] = useState<number | null>(null);
  const loaded = loadedFrame === frame.id;
  const target = useRef<HTMLDivElement>(null);
  // Hands a preloaded frame its session, once the page has one.
  const starter = useRef<((session: string) => void) | null>(null);
  useEffect(() => {
    const at = new URL(page);
    const organizer = at.pathname.split("/").filter(Boolean).at(-1)!;
    const madeFor = frame.madeFor;
    const frameId = frame.id;
    let cancelled = false;
    let loader: Loader | undefined;
    let preloaded: ReturnType<NonNullable<Loader["preload"]>> | undefined;
    let instance: Instance | undefined;
    let cleanupRoom = () => {};
    let stopFollowingKeyboard = () => {};
    // The page's first token for the session, asked for as soon as there
    // is a session: in parallel with the script and the frame loading, or
    // with a preloaded frame starting.
    let handed: Promise<string | null> | null | undefined;
    const askHanded = () => {
      if (handed === undefined)
        handed =
          identity === "host" && attest.current
            ? attest.current().catch(() => null)
            : null;
      return handed;
    };
    // The fan sheet's own tokens theme the embed, for every organizer and
    // with nothing to set in ticketing-admin: fonts, the white/grey text,
    // the translucent fields and the card fields inside them all match the
    // sheet around them, and its background shows through. The organizer's
    // stored branding still themes the hosted page the mobile app opens.
    const frameOptions: FrameOptions = {
      organizer,
      appearance,
      ...(identity === "host" ? { identity } : {}),
      ...(identity === "host" && contact === "fixed" ? { contact } : {}),
      ...(artist && around ? { artist, ancestors: around.split(" ") } : {}),
    };
    const sessionOptions = (session: string): SessionOptions => {
      const token = askHanded();
      return {
        session,
        ...(token ? { attestation: token } : {}),
        ...(timed.current ? { timing: timed.current } : {}),
        ...(answers
          ? {
              checkoutToken: (request: CheckoutTokenRequest) =>
                answer.current
                  ? answer.current(request)
                  : Promise.reject(new Error("no_token")),
            }
          : {}),
      };
    };
    const wire = (checkout: Instance) => {
      instance = checkout;
      const sheet = target.current?.closest<HTMLElement>(
        ".music-access-sheet, .tickets-sheet-panel",
      );
      checkout.on("change_contact", () => changeContact.current?.());
      checkout.on("accepted", () => paid.current?.());
      checkout.on("succeeded", (payload) => {
        const value = payload as Partial<EngineCheckoutSucceeded> | undefined;
        succeeded.current?.({
          total_label:
            typeof value?.total_label === "string" ? value.total_label : "",
          method_label:
            typeof value?.method_label === "string" ? value.method_label : null,
          buyer_email:
            typeof value?.buyer_email === "string" ? value.buyer_email : null,
          status_origin: onPage(
            value?.status_origin,
            target.current?.querySelector("iframe"),
          ),
        });
      });
      checkout.on("session", (payload) => {
        setLoadedFrame(frameId);
        const expiresAt = (payload as { expires_at?: unknown } | undefined)
          ?.expires_at;
        if (typeof expiresAt === "string") held.current?.(expiresAt);
      });
      checkout.on("expired", () =>
        expired.current
          ? expired.current()
          : errored.current("Checkout expired. Choose your tickets again."),
      );
      checkout.on("paying", () => payingNow.current?.(true));
      // Chrome on iOS shrinks the viewport for the keyboard but does not
      // scroll a field inside a cross-origin frame into the part that is
      // left; Safari does. So the drop-in says where the field is and the
      // sheet scrolls it clear itself — now, and when the viewport tells us
      // the keyboard has taken its room, not on timers: timed checks moved
      // the sheet in steps after the keyboard, as if catching up
      // (2026-10-28). Where the browser has already scrolled the field
      // clear there is nothing left to do.
      checkout.on("focus", (payload) => {
        const bottom = (payload as { bottom?: unknown } | undefined)?.bottom;
        if (typeof bottom !== "number") return;
        stopFollowingKeyboard();
        const clear = () => {
          const iframe = target.current?.querySelector("iframe");
          if (!iframe || !sheet) return;
          const viewport = window.visualViewport;
          const visibleBottom = viewport
            ? viewport.offsetTop + viewport.height
            : window.innerHeight;
          const fieldBottom = iframe.getBoundingClientRect().top + bottom;
          const overflow = fieldBottom - (visibleBottom - 24);
          if (overflow > 0)
            sheet.scrollBy({ top: overflow, behavior: "smooth" });
        };
        clear();
        const viewport = window.visualViewport;
        if (!viewport) {
          const timer = window.setTimeout(clear, 350);
          stopFollowingKeyboard = () => window.clearTimeout(timer);
          return;
        }
        let frame = 0;
        const onViewport = () => {
          window.cancelAnimationFrame(frame);
          frame = window.requestAnimationFrame(clear);
        };
        viewport.addEventListener("resize", onViewport);
        viewport.addEventListener("scroll", onViewport);
        // The keyboard is up well within this; later viewport changes
        // (a pinch, the toolbar) are the reader's, not ours to follow.
        const done = window.setTimeout(() => stopFollowingKeyboard(), 1000);
        stopFollowingKeyboard = () => {
          window.cancelAnimationFrame(frame);
          window.clearTimeout(done);
          viewport.removeEventListener("resize", onViewport);
          viewport.removeEventListener("scroll", onViewport);
          stopFollowingKeyboard = () => {};
        };
      });
      // Tell the drop-in how much of the sheet is left below the event
      // header, so it fills it and keeps the pay controls at the bottom
      // like a native sheet. Re-measured whenever the sheet resizes.
      // The tickets sheet (host identity) is the other way round: the sheet
      // takes the checkout's own height, so it is never asked to fill one.
      const room = () => {
        if (identity === "host" || !sheet || !target.current) return;
        const top =
          target.current.getBoundingClientRect().top -
          sheet.getBoundingClientRect().top +
          sheet.scrollTop;
        const minHeight = Math.floor(sheet.clientHeight - top - 24);
        if (minHeight > 0) checkout.update({ minHeight });
      };
      room();
      const observer =
        typeof ResizeObserver === "undefined" || !sheet
          ? null
          : new ResizeObserver(room);
      if (sheet) observer?.observe(sheet);
      cleanupRoom = () => observer?.disconnect();
      checkout.on("failed", () => {
        payingNow.current?.(false);
        // Host mode: the drop-in says what went wrong itself, in place (a
        // declined card beside the card it was), so the page adds nothing.
        if (identity !== "host")
          errored.current("Checkout could not complete. Please try again.");
      });
    };
    /** The checkout for a session: the preloaded frame started with it, or
     *  a frame mounted for it. */
    const start = (session: string) => {
      if (cancelled || instance || !loader || !target.current) return;
      let started: Instance | undefined;
      if (preloaded)
        try {
          started = preloaded.start(sessionOptions(session));
        } catch {
          // A preload that cannot start (its frame no longer in the page)
          // gives way to a frame mounted for the session.
          preloaded.destroy();
          preloaded = undefined;
        }
      wire(
        started ??
          loader.mount(target.current, {
            ...frameOptions,
            ...sessionOptions(session),
          }),
      );
    };
    if (madeFor !== null) askHanded();
    // Ahead of a session a checkout.js that did not load is nobody's problem
    // yet: it is loaded again when the session comes, and only then said.
    let retry = false;
    const failed = () => {
      if (cancelled) return;
      if (madeFor === null && latest.current === null) retry = true;
      else errored.current("Could not load checkout. Please try again.");
    };
    let script: HTMLScriptElement | undefined;
    const load = () => {
      script?.remove();
      const next = document.createElement("script");
      next.src = `${at.origin}/checkout.js`;
      next.onload = () => {
        if (cancelled || !target.current) return;
        loader = (window as Window & { LissenCheckout?: Loader })
          .LissenCheckout;
        if (!loader) return failed();
        const session = madeFor ?? latest.current;
        if (session !== null) start(session);
        // No session yet: the frame is made now, here, and started when the
        // session comes. A checkout.js from before preloading mounts one
        // then.
        else if (typeof loader.preload === "function")
          preloaded = loader.preload(target.current, frameOptions);
      };
      next.onerror = failed;
      document.head.appendChild(next);
      script = next;
    };
    starter.current = (session) => {
      askHanded();
      if (!retry) return start(session);
      retry = false;
      load();
    };
    load();
    return () => {
      cancelled = true;
      starter.current = null;
      cleanupRoom();
      stopFollowingKeyboard();
      (instance ?? preloaded)?.destroy();
      script?.remove();
    };
  }, [
    frame.id,
    frame.madeFor,
    page,
    identity,
    contact,
    appearance,
    answers,
    artist,
    around,
  ]);
  // The session for a frame made ahead of it.
  useEffect(() => {
    if (frame.madeFor === null && frame.session !== null)
      starter.current?.(frame.session);
  }, [frame.id, frame.madeFor, frame.session]);
  if (identity !== "host") return <div ref={target} />;
  // Host mode: the sheet's own checkout shape stays over the frame until the
  // checkout reports its session. The frame paints nothing for a moment after
  // it mounts, and its loader starts at a default height; the shape (the same
  // rows as the checkout's own loading state) covers both, so nothing blinks
  // and nothing moves.
  return (
    <div
      style={{
        minHeight: loaded ? undefined : CHECKOUT_SHAPE_HEIGHT,
        position: "relative",
      }}
    >
      <div ref={target} style={{ opacity: loaded ? 1 : 0 }} />
      {loaded ? null : (
        <div style={{ inset: 0, position: "absolute" }}>{placeholder}</div>
      )}
    </div>
  );
}

/** Keep in step with the purchase sheet's skeleton (purchase-sheet.tsx). */
const CHECKOUT_SHAPE_HEIGHT = 283;
